(function () {
  const TYPE_MAP = {
    fade: 'home-reveal--fade',
    'fade-up': 'home-reveal--up',
    'fade-down': 'home-reveal--down',
    'fade-left': 'home-reveal--left',
    'fade-right': 'home-reveal--right',
    scale: 'home-reveal--scale',
    pop: 'home-reveal--pop',
    'blur-up': 'home-reveal--blur-up',
    'tilt-left': 'home-reveal--tilt-left',
    'tilt-right': 'home-reveal--tilt-right',
    up: 'home-reveal--up',
    down: 'home-reveal--down',
    left: 'home-reveal--left',
    right: 'home-reveal--right',
  };

  const REVEAL_TYPES = Object.values(TYPE_MAP).filter((value, index, list) => list.indexOf(value) === index);

  const DAWN_ANIMATION_CLASSES = [
    'scroll-trigger',
    'animate--slide-in',
    'animate--fade-in',
    'animate--slide-up',
    'animate--fade-up',
    'animate--slide-left',
    'animate--slide-right',
    'animate--zoom-in',
  ];

  const SKIP_SELECTORS = [
    '.header',
    '.header-wrapper',
    'predictive-search',
    'cart-drawer',
  ];

  let observer = null;
  let initialCheckDone = false;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isSliderSlide(element) {
    if (!element) return false;
    return !!(element.classList.contains('slider__slide') || element.closest('.slider > .grid__item, .slider > li'));
  }

  function isMobileSliderItem(element) {
    return isSliderSlide(element);
  }

  function stripDawnAnimations(element) {
    if (!element || !element.classList) return;
    DAWN_ANIMATION_CLASSES.forEach((className) => element.classList.remove(className));
    element.classList.remove('scroll-trigger--offscreen', 'scroll-trigger--design-mode');
    element.removeAttribute('data-cascade');
    element.style.removeProperty('--animation-order');
  }

  function applyReveal(element, type, order, duration, delay, options) {
    if (!element || isMobileSliderItem(element)) return;
    if (element.hasAttribute('data-section-fade')) return;
    if (element.dataset.fadePlayed === 'true') return;

    stripDawnAnimations(element);
    REVEAL_TYPES.forEach((className) => element.classList.remove(className));
    element.classList.add('home-reveal', type);
    element.classList.remove('is-visible');

    if (typeof order === 'number') {
      element.style.setProperty('--reveal-order', order);
    } else {
      element.style.removeProperty('--reveal-order');
    }

    element.style.setProperty('--home-reveal-duration', `${duration || 1000}ms`);
    element.style.setProperty('--home-reveal-delay', `${delay || 0}ms`);

    if (options && options.playOnLoad) {
      element.dataset.fadePlayed = 'true';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          element.classList.add('is-visible');
        });
      });
      return;
    }

    if (observer) observer.observe(element);
  }

  function applyReveals(elements, type, startOrder = 0, duration, delay, options) {
    elements.forEach((element, index) => applyReveal(element, type, startOrder + index, duration, delay, options));
  }

  function unique(elements) {
    return elements.filter((element, index, list) => element && list.indexOf(element) === index);
  }

  function topMost(elements) {
    const list = unique(elements);
    return list.filter((element) => !list.some((other) => other !== element && other.contains(element)));
  }

  function shouldSkipSection(section) {
    if (!section) return true;
    if (section.classList.contains('announcement-bar-section')) return true;
    if (section.closest('.shopify-section-group-header-group')) return true;
    return SKIP_SELECTORS.some((selector) => section.matches(selector) || section.querySelector(`:scope > ${selector}`));
  }

  function getFadeConfig(section) {
    const config = section.querySelector('[data-section-fade]');
    const enabled = !config || config.dataset.sectionFade !== 'false';
    const typeKey = config?.dataset.fadeType || 'fade-up';
    return {
      enabled,
      type: TYPE_MAP[typeKey] || 'home-reveal--up',
      duration: Number(config?.dataset.fadeDuration) || 1000,
      delay: Number(config?.dataset.fadeDelay) || 0,
    };
  }

  function getProductCollectionTargets(section) {
    if (!section.querySelector('.product-grid')) return [];

    const intro = topMost(
      Array.from(
        section.querySelectorAll(
          '.collection__description, .new-view, .collection__title .title, .related-products__heading, .c-btn'
        )
      )
    );
    const grid = section.querySelector('.product-grid');
    const sliderActive =
      grid &&
      grid.classList.contains('slider') &&
      ((grid.classList.contains('slider--tablet') && window.matchMedia('(max-width: 989px)').matches) ||
        (grid.classList.contains('slider--desktop') && window.matchMedia('(min-width: 990px)').matches) ||
        (grid.classList.contains('slider--mobile') && window.matchMedia('(max-width: 749px)').matches));

    if (sliderActive) {
      const slider = section.querySelector('slider-component') || grid;
      return unique([...intro, slider]);
    }

    const cards = Array.from(
      grid.querySelectorAll(':scope > .grid__item .card-wrapper, :scope > .grid__item .card, :scope > .grid__item')
    );
    const cardTargets = cards.filter((element) => !isSliderSlide(element) && !element.classList.contains('grid__item'));
    const fallbackItems = cardTargets.length
      ? cardTargets
      : Array.from(grid.querySelectorAll(':scope > .grid__item')).filter((element) => !isSliderSlide(element));

    return unique([...intro, ...fallbackItems]);
  }

  function getTargets(section) {
    const productCollectionTargets = getProductCollectionTargets(section);
    if (productCollectionTargets.length) return productCollectionTargets;

    const specific = [
      section.querySelectorAll('.marquee-content'),
      section.querySelectorAll('.naples-collection-grid__header, .naples-collection-grid__item'),
      section.querySelectorAll(
        '.wholesale-trade__overline, .wholesale-trade__heading, .wholesale-trade__description, .wholesale-trade__button, .wholesale-trade__card'
      ),
      section.querySelectorAll(
        '.subscribe-save__badge, .subscribe-save__heading, .subscribe-save__description, .subscribe-save__button, .subscribe-save__step'
      ),
      section.querySelectorAll(
        '.pizza-love__media-wrap, .pizza-love__overline, .pizza-love__heading, .pizza-love__description, .pizza-love__button'
      ),
      section.querySelectorAll('.naples-newsletter__media, .naples-newsletter__heading, .newsletter__subheading, .naples-newsletter-form__row'),
      section.querySelectorAll('.multicolumn-list__item, .collection-list__item, .collage__item, .blog-articles .article'),
      section.querySelectorAll('.image-with-text, .rich-text__wrapper, .contact, .video-section, .banner, .page-width > .rte'),
    ];

    for (const nodeList of specific) {
      const items = unique(Array.from(nodeList));
      if (items.length) return items;
    }

    const root = Array.from(section.children).find((child) => !child.hasAttribute('data-section-fade'));
    return root ? [root] : [];
  }

  function setupSection(section) {
    if (shouldSkipSection(section)) return;

    const { enabled, type, duration, delay } = getFadeConfig(section);
    if (!enabled) return;

    applyReveals(getTargets(section), type, 0, duration, delay);
  }

  function revealElement(element, instant) {
    if (element.dataset.fadePlayed === 'true') {
      if (observer) observer.unobserve(element);
      return;
    }
    element.dataset.fadePlayed = 'true';
    if (instant) element.classList.add('home-reveal--instant');
    element.classList.add('is-visible');
    if (observer) observer.unobserve(element);
  }

  function createObserver() {
    if (prefersReducedMotion()) return null;

    return new IntersectionObserver(
      (entries) => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const top = entry.boundingClientRect.top;
          const alreadyOnScreen = top < viewportHeight * 0.72;

          if (!initialCheckDone && alreadyOnScreen) {
            revealElement(entry.target, true);
            return;
          }

          if (top < viewportHeight * 0.9) {
            revealElement(entry.target, false);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -12% 0px',
        threshold: [0, 0.1, 0.2, 0.35],
      }
    );
  }

  function getSections() {
    return unique([
      ...document.querySelectorAll('#MainContent .shopify-section'),
      ...document.querySelectorAll('.shopify-section-group-footer-group .shopify-section'),
    ]);
  }

  function initSectionFadeAnimations() {
    if (observer) observer.disconnect();
    observer = createObserver();

    document.body.classList.add('home-animated', 'section-animated');
    getSections().forEach(setupSection);

    if (prefersReducedMotion()) {
      document.querySelectorAll('.home-reveal').forEach((element) => {
        element.classList.add('is-visible', 'home-reveal--instant');
      });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        initialCheckDone = true;
      });
    });
  }

  function bootFadeAnimations() {
    if (window.__naplesFadeBooted) return;
    window.__naplesFadeBooted = true;
    initSectionFadeAnimations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootFadeAnimations);
  } else {
    bootFadeAnimations();
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      if (!observer) observer = createObserver();
      window.__naplesFadeBooted = true;
      setupSection(event.target);
    });
  }
})();
